import {
  AdminTableContainer,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Input,
  Label,
  sonnerToast,
} from "@simplicity/ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { apiFetch } from "../../api";
import type { FeatureFlag } from "../../configTypes";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isStale(staleAfter: string | null): boolean {
  if (!staleAfter) return false;
  return new Date(staleAfter) < new Date();
}

interface CreateFlagFormFieldsProps {
  key_: string;
  setKey: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  defaultValue: boolean;
  setDefaultValue: (v: boolean) => void;
  staleAfter: string;
  setStaleAfter: (v: string) => void;
}

function CreateFlagFormFields({
  key_,
  setKey,
  description,
  setDescription,
  defaultValue,
  setDefaultValue,
  staleAfter,
  setStaleAfter,
}: CreateFlagFormFieldsProps) {
  return (
    <>
      <div className="mb-3">
        <Label htmlFor="flag-key" className="form-label">
          Key
        </Label>
        <Input
          id="flag-key"
          value={key_}
          onChange={(e) => setKey(e.target.value)}
          placeholder="domain.feature_name"
          required
          pattern="[\w.]+"
          title="Alphanumeric with dots and underscores"
          className="max-w-sm"
        />
      </div>
      <div className="mb-3">
        <Label htmlFor="flag-description" className="form-label">
          Description
        </Label>
        <Input
          id="flag-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this flag controls"
          required
          className="max-w-sm"
        />
      </div>
      <div className="mb-3 flex items-center gap-3">
        <Checkbox
          id="flag-default"
          checked={defaultValue}
          onCheckedChange={(v) => setDefaultValue(v === true)}
        />
        <Label htmlFor="flag-default" className="form-label mb-0">
          Default enabled
        </Label>
      </div>
      <div className="mb-4">
        <Label htmlFor="flag-stale" className="form-label">
          Stale after (optional)
        </Label>
        <Input
          id="flag-stale"
          type="date"
          value={staleAfter}
          onChange={(e) => setStaleAfter(e.target.value)}
          className="max-w-48"
        />
      </div>
    </>
  );
}

function CreateFlagForm({ onCreated }: { onCreated: (flag: FeatureFlag) => void }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [defaultValue, setDefaultValue] = useState(false);
  const [staleAfter, setStaleAfter] = useState("");
  const [saving, setSaving] = useState(false);

  function reset() {
    setKey("");
    setDescription("");
    setDefaultValue(false);
    setStaleAfter("");
    setOpen(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const flag = await apiFetch<FeatureFlag>("/api/feature-flags", {
        method: "POST",
        body: JSON.stringify({
          key,
          description,
          defaultValue,
          staleAfter: staleAfter ? new Date(staleAfter).toISOString() : null,
        }),
      });
      onCreated(flag);
      sonnerToast.success(`Flag "${flag.key}" created`);
      reset();
    } catch (err) {
      sonnerToast.error(err instanceof Error ? err.message : "Failed to create flag");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <Button variant="outline" onClick={() => setOpen(true)}>
        Create
      </Button>
    );
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="rounded-md border border-border bg-card p-4"
    >
      <h2 className="mb-3 text-sm font-semibold text-foreground">New Feature Flag</h2>
      <CreateFlagFormFields
        key_={key}
        setKey={setKey}
        description={description}
        setDescription={setDescription}
        defaultValue={defaultValue}
        setDefaultValue={setDefaultValue}
        staleAfter={staleAfter}
        setStaleAfter={setStaleAfter}
      />
      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          {saving ? "Creating..." : "Create"}
        </Button>
        <Button type="button" variant="outline" onClick={reset}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function FeatureFlagsList() {
  const navigate = useNavigate();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void apiFetch<FeatureFlag[]>("/api/feature-flags")
      .then(setFlags)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="empty-state">Loading feature flags...</div>;
  }

  if (error) {
    return <div className="empty-state text-destructive">{error}</div>;
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl text-foreground">Feature Flags</h2>
        <CreateFlagForm
          onCreated={(flag) =>
            setFlags((prev) => [...prev, flag].sort((a, b) => a.key.localeCompare(b.key)))
          }
        />
      </div>
      <AdminTableContainer aria-label="Feature flags">
        {flags.length === 0 ? (
          <EmptyState title="No feature flags defined" />
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Default</th>
                <th>Description</th>
                <th>Overrides</th>
                <th>Stale After</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr
                  key={f.key}
                  className="clickable"
                  onClick={() =>
                    void navigate(`/config/feature-flags/${encodeURIComponent(f.key)}`)
                  }
                >
                  <td className="font-mono text-sm">{f.key}</td>
                  <td>
                    <Badge variant={f.defaultValue ? "success" : "secondary"}>
                      {f.defaultValue ? "ON" : "OFF"}
                    </Badge>
                  </td>
                  <td className="col-muted">{f.description}</td>
                  <td>{f.overrideCount > 0 ? f.overrideCount : "—"}</td>
                  <td className="col-muted">{f.staleAfter ? formatDate(f.staleAfter) : "—"}</td>
                  <td>{isStale(f.staleAfter) ? <Badge variant="warning">Stale</Badge> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </AdminTableContainer>
    </div>
  );
}
